import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider } from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHomeSpaces } from '@app/actions/home/fetchHomeSpaces';
import { fetchRecentlyViewedSpaces } from '@core/spaceSelector/operations';
import useBreakpoints from '@app/hooks/useBreakpoints';
import {
  getCanSwitchHomeViewRole,
  getHomeSpaceCardsForView,
  getHomeSpacesError,
  getHomeSpacesErrorType,
  getHomeSpacesHasMore,
  getHomeSpacesLoading,
  getHomeViewRole,
} from '@app/selectors/home';
import HomeGreeting from '../greeting';
import SpaceWidgetCarousel from './SpaceWidgetCarousel';
import SpaceWidgetEmptyState from './SpaceWidgetEmptyState';
import SpaceWidgetNavArrows from './SpaceWidgetNavArrows';
import SpaceWidgetToolbar from './SpaceWidgetToolbar';
import SpaceWidgetSkeleton from './SpaceWidgetSkeleton';
import {
  SPACE_WIDGET_CONTENT_GAP,
  SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_LG,
  SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_SM,
  SPACE_WIDGET_PAGINATION_ERROR_THRESHOLD,
  SPACE_WIDGET_SCROLL_PREFETCH_REMAINING_SLIDES,
  SPACE_WIDGET_SKELETON_CARD_COUNT_LG,
  SPACE_WIDGET_SKELETON_CARD_COUNT_SM,
  SPACE_WIDGET_TOOLBAR_GAP,
} from './spaceWidgetConstants';
import {
  SpaceWidgetCardData,
  SpaceWidgetSectionProps,
} from './SpaceWidget.types';
import { useSpaceWidgetSwiper } from './useSpaceWidgetSwiper';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';

type SpaceWidgetSectionPropsWithDefaults = SpaceWidgetSectionProps & {
  spaces?: SpaceWidgetCardData[];
};

const SpaceWidgetSection = ({
  showRoleToggle = true,
  showGreeting = true,
  showSpaceCarousel = true,
  spaces: spacesOverride,
}: SpaceWidgetSectionPropsWithDefaults) => {
  const dispatch = useDispatch();
  const { lg: isLgUp } = useBreakpoints();
  const viewRole = useSelector(getHomeViewRole);
  const canSwitchRole = useSelector(getCanSwitchHomeViewRole);
  const spacesFromStore = useSelector(getHomeSpaceCardsForView);
  const spacesLoading = useSelector(getHomeSpacesLoading);
  const spacesHasMore = useSelector(getHomeSpacesHasMore);
  const spacesError = useSelector(getHomeSpacesError);
  const spacesErrorType = useSelector(getHomeSpacesErrorType);
  const [initialSpacesFetchSettled, setInitialSpacesFetchSettled] = useState(
    Boolean(spacesOverride)
  );
  const hasRequestedSpacesRef = useRef(Boolean(spacesOverride));
  const wasSpacesLoadingRef = useRef(false);
  const visibleSpaceCountAtFetchStartRef = useRef(0);
  const [paginationFailureCount, setPaginationFailureCount] = useState(0);
  const visibleSpaces = useMemo(() => {
    if (spacesOverride) {
      return spacesOverride.filter((space) => space.role === viewRole);
    }

    return spacesFromStore;
  }, [spacesFromStore, spacesOverride, viewRole]);
  const skeletonCardCount = isLgUp
    ? SPACE_WIDGET_SKELETON_CARD_COUNT_LG
    : SPACE_WIDGET_SKELETON_CARD_COUNT_SM;
  const previouslyFetchedWithNoMore =
    !spacesOverride && !spacesLoading && !spacesHasMore;
  const showSpacesSkeleton =
    !spacesOverride &&
    !spacesError &&
    spacesLoading &&
    visibleSpaces.length === 0;
  const spacesSettled = spacesOverride
    ? true
    : previouslyFetchedWithNoMore ||
      spacesError ||
      (!spacesLoading && initialSpacesFetchSettled);
  const showErrorState =
    !spacesOverride &&
    showSpaceCarousel &&
    spacesError &&
    !spacesLoading &&
    visibleSpaces.length === 0;
  const showPaginationErrorCard =
    !spacesOverride &&
    showSpaceCarousel &&
    visibleSpaces.length > 0 &&
    spacesHasMore &&
    !spacesLoading &&
    paginationFailureCount >= SPACE_WIDGET_PAGINATION_ERROR_THRESHOLD;
  const showCarouselErrorCard = showErrorState || showPaginationErrorCard;
  const showEmptyState =
    showSpaceCarousel &&
    !spacesError &&
    !spacesLoading &&
    spacesSettled &&
    visibleSpaces.length === 0;
  const minVisibleCards = isLgUp
    ? SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_LG
    : SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_SM;
  const showTrailingSkeletons =
    !spacesOverride &&
    spacesLoading &&
    spacesHasMore &&
    visibleSpaces.length > 0 &&
    !showPaginationErrorCard &&
    !showErrorState;
  const trailingSkeletonCount = showTrailingSkeletons
    ? Math.max(1, minVisibleCards - visibleSpaces.length)
    : 0;
  const carouselItemCount =
    (showSpaceCarousel ? visibleSpaces.length : 0) +
    trailingSkeletonCount +
    (showCarouselErrorCard ? 1 : 0);

  useTrackHomeWidgetLoaded(
    'spaces',
    !spacesOverride && spacesSettled && !spacesError,
    visibleSpaces.length
  );

  const loadMoreSpaces = () => {
    if (spacesOverride || showPaginationErrorCard || showErrorState) {
      return;
    }

    dispatch(fetchHomeSpaces() as any);
  };

  const handleRetrySpaces = () => {
    if (spacesOverride) {
      return;
    }

    dispatch(fetchHomeSpaces() as any);
  };

  const shouldAutoPrefetch =
    !spacesOverride &&
    !spacesLoading &&
    !spacesError &&
    spacesHasMore &&
    visibleSpaces.length < minVisibleCards;

  const {
    handleSwiper,
    handleSlideChange,
    handleReachEnd,
    handleTransitionEnd,
    canScrollBack,
    canScrollForward,
    handleScroll,
  } = useSpaceWidgetSwiper({
    itemCount: carouselItemCount,
    hasMore: spacesOverride ? false : spacesHasMore,
    loading: spacesOverride ? false : spacesLoading,
    onNearEnd: loadMoreSpaces,
    resetKey: viewRole,
    prefetchRemainingSlides: SPACE_WIDGET_SCROLL_PREFETCH_REMAINING_SLIDES,
  });

  useEffect(() => {
    if (spacesLoading) {
      hasRequestedSpacesRef.current = true;

      if (!wasSpacesLoadingRef.current) {
        visibleSpaceCountAtFetchStartRef.current = visibleSpaces.length;
      }
    }

    if (
      wasSpacesLoadingRef.current &&
      !spacesLoading &&
      !spacesOverride &&
      spacesError &&
      visibleSpaces.length > 0 &&
      visibleSpaces.length === visibleSpaceCountAtFetchStartRef.current
    ) {
      setPaginationFailureCount((count) => count + 1);
    }

    if (
      wasSpacesLoadingRef.current &&
      !spacesLoading &&
      !spacesError &&
      visibleSpaces.length > visibleSpaceCountAtFetchStartRef.current
    ) {
      setPaginationFailureCount(0);
    }

    wasSpacesLoadingRef.current = spacesLoading;
  }, [spacesLoading, spacesError, spacesOverride, visibleSpaces.length]);

  useEffect(() => {
    setPaginationFailureCount(0);
  }, [viewRole]);

  useEffect(() => {
    if (spacesOverride) {
      return;
    }

    if (spacesError) {
      setInitialSpacesFetchSettled(true);
      return;
    }

    if (!spacesLoading && !spacesHasMore) {
      setInitialSpacesFetchSettled(true);
      return;
    }

    if (!spacesLoading && hasRequestedSpacesRef.current) {
      setInitialSpacesFetchSettled(true);
    }
  }, [spacesOverride, spacesLoading, spacesError, spacesHasMore]);

  useEffect(() => {
    if (!shouldAutoPrefetch) {
      return;
    }

    dispatch(fetchHomeSpaces() as any);
  }, [dispatch, shouldAutoPrefetch]);

  useEffect(() => {
    if (spacesOverride) {
      return;
    }

    dispatch(fetchRecentlyViewedSpaces() as any);
  }, [dispatch, spacesOverride]);

  const showToolbar = showRoleToggle && canSwitchRole;
  const showNavigation = showSpaceCarousel && !showEmptyState;
  const showGreetingRow = showGreeting || showNavigation;
  const showCarouselRow = showSpaceCarousel;
  const showSpacesContent = showCarouselRow || showGreetingRow;

  if (!showToolbar && !showCarouselRow && !showGreeting) {
    return null;
  }

  return (
    <Box
      width="100%"
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: SPACE_WIDGET_TOOLBAR_GAP,
      }}
    >
      {showToolbar ? <SpaceWidgetToolbar showRoleToggle /> : null}

      {showToolbar && showSpacesContent ? <Divider /> : null}

      {showSpacesContent ? (
        <Box
          width="100%"
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: SPACE_WIDGET_CONTENT_GAP,
          }}
        >
          {showGreetingRow ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
                minWidth: 0,
              }}
            >
              {showGreeting ? (
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <HomeGreeting />
                </Box>
              ) : (
                <Box sx={{ flex: 1, minWidth: 0 }} />
              )}

              {showNavigation ? (
                <SpaceWidgetNavArrows
                  canScrollBack={canScrollBack}
                  canScrollForward={canScrollForward}
                  onScrollBack={() => handleScroll('back')}
                  onScrollForward={() => handleScroll('forward')}
                />
              ) : null}
            </Box>
          ) : null}

          {showCarouselRow ? (
            <Box
              id="home-space-widget"
              aria-label="Space Widget"
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {showSpacesSkeleton ? (
                <SpaceWidgetSkeleton cardCount={skeletonCardCount} />
              ) : showEmptyState ? (
                <SpaceWidgetEmptyState />
              ) : (
                <SpaceWidgetCarousel
                  spaces={showSpaceCarousel ? visibleSpaces : []}
                  showErrorCard={showCarouselErrorCard}
                  errorVariant={spacesErrorType ?? 'api'}
                  onErrorRetry={handleRetrySpaces}
                  errorRetrying={spacesLoading && showErrorState}
                  trailingSkeletonCount={trailingSkeletonCount}
                  onSwiper={handleSwiper}
                  onSlideChange={handleSlideChange}
                  onReachEnd={handleReachEnd}
                  onTransitionEnd={handleTransitionEnd}
                />
              )}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {showSpacesContent ? <Divider /> : null}
    </Box>
  );
};

export default SpaceWidgetSection;
